import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { API_STATUS } from "../types"
import { LEVERAGE_DROPDOWN, NULLABLE_BOOLEAN_DROPDOWN, SRRI_DROPDOWN } from "../../constants/common"
import { customValuesInInstrumentStageSchema } from "../../utils/FormEngine/CustomFieldsRepository/instrumentManagement"
import { EnumItem } from "../../types/common"
import { updateFrequencyEnum } from "../../utils/common"

import { utilitiesSliceInitialState } from "./types"
import {
  ACTION_TYPE_PARENT,
  fetchModelSettings,
  fetchDropDownList,
  fetchEnumList,
  fetchPortfolioAllocationTypes,
  clearModelSettings,
  fetchCountriesList,
  fetchInstrumentModelSchema,
  fetchInstrumentHistoryModelSchema,
  fetchListingExchanges,
  fetchMarketCloseDays,
  fetchMarketCloseDaysCustom
} from "./actions"

const INITIAL_STATE: utilitiesSliceInitialState = {
  originalProductModelSchema: [],
  dropDownLists: {},
  enumLists: [],
  portfolioAllocationTypesList: [],
  combinedListOfOptions: {},
  status: API_STATUS.idle,
  isLoading: false,
  error: null,
  countriesList: [],
  originalInstrumentModelSchema: [],
  originalInstrumentHistoryModelSchema: [],
  listingExchangesList : [],
  nasdaqHolidays: [],
  customHolidays: []
}

/* eslint-disable no-param-reassign */
export const utilitiesSlice = createSlice({
  name: ACTION_TYPE_PARENT,
  initialState: INITIAL_STATE,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchModelSettings.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.status = API_STATUS.success
        state.originalProductModelSchema = [...state.originalProductModelSchema, ...action.payload]
      })
      .addCase(fetchModelSettings.pending, (state) => {
        state.status = API_STATUS.loading
      })
      .addCase(fetchModelSettings.rejected, (state, action: any) => {
        state.status = API_STATUS.failed
        // state.error = action?.error?.message
        state.error = action.payload
      })
      .addCase(clearModelSettings.fulfilled, (state, action: any) => {
        state.status = API_STATUS.success
        state.originalProductModelSchema = action.payload
        state.originalInstrumentModelSchema = action.payload
      })
      .addCase(clearModelSettings.pending, (state) => {
        state.status = API_STATUS.loading
      })
      .addCase(clearModelSettings.rejected, (state, action: any) => {
        state.status = API_STATUS.failed
        // state.error = action?.error?.message
        state.error = action.payload
      })
      .addCase(fetchDropDownList.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.status = API_STATUS.success
        const temp: any = { ...action.payload }
        Object.keys(temp).forEach((key) => {
          if (key) {
            temp[key]?.forEach((item: any) => {
              item.value = item.description
            })
          }
        })
        // LeverageAmountLimits represents the fieldName
        temp.LeverageAmountLimits = LEVERAGE_DROPDOWN
        // SRRI dropdown in instrument model
        temp.SRRI= SRRI_DROPDOWN
        // sort instrument class inplace
        temp.InstrumentClass = temp.InstrumentClass.sort((a:any ,b:any) => (`${a.description.trim()}`).localeCompare(b.description.trim(), undefined, {numeric: true}))
        state.dropDownLists = temp
        state.combinedListOfOptions = { ...state.combinedListOfOptions, ...temp }
      })
      .addCase(fetchDropDownList.pending, (state) => {
        state.status = API_STATUS.loading
      })
      .addCase(fetchDropDownList.rejected, (state, action: any) => {
        state.status = API_STATUS.failed
        state.error = action.payload
      })
      .addCase(fetchEnumList.fulfilled, (state, action: PayloadAction<EnumItem[]>) => {
        state.status = API_STATUS.success
        // Adding Boolean in enum because value != desc in case of enums
        let array: EnumItem[] = [...action.payload, { key: "Boolean", value: NULLABLE_BOOLEAN_DROPDOWN }]
        array.forEach((item : EnumItem) => {
          if(item.key === "InstrumentCategory6Enum"){
            item.value = item.value.sort((a ,b) => (`${a.description.trim()}`).localeCompare(b.description.trim(), undefined, {numeric: true}))
          }
        })
        // Remove NA and Other options from pricing frequency
        array = updateFrequencyEnum(array)

        state.enumLists = array

        const tempEnumCombinedList: any = {}
        array.forEach((item: EnumItem) => {
          tempEnumCombinedList[item.key] = item.value
        })
        const comboList = { ...state.combinedListOfOptions, ...tempEnumCombinedList }
        const tempPayload: any = {}
        Object.keys(comboList).forEach((key) => {
          if (key) {
            tempPayload[key] = comboList[key]
          }
        })
        state.combinedListOfOptions = tempPayload
      })
      .addCase(fetchEnumList.pending, (state) => {
        state.status = API_STATUS.loading
      })
      .addCase(fetchEnumList.rejected, (state, action: any) => {
        state.status = API_STATUS.failed
        state.error = action.payload
      })
      .addCase(fetchPortfolioAllocationTypes.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.status = API_STATUS.success
        state.portfolioAllocationTypesList = action.payload
      })
      .addCase(fetchPortfolioAllocationTypes.pending, (state) => {
        state.status = API_STATUS.loading
      })
      .addCase(fetchPortfolioAllocationTypes.rejected, (state, action: any) => {
        state.status = API_STATUS.failed
        // state.error = action?.error?.message
        state.error = action.payload
      })

      .addCase(fetchCountriesList.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.status = API_STATUS.success
        const response: any = { ...action.payload }
        state.countriesList = response.countries
      })
      .addCase(fetchCountriesList.pending, (state) => {
        state.status = API_STATUS.loading
      })
      .addCase(fetchCountriesList.rejected, (state, action: any) => {
        state.status = API_STATUS.failed
        // state.error = action?.error?.message
        state.error = action.payload
      })

      .addCase(fetchInstrumentModelSchema.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.status = API_STATUS.success
        state.originalInstrumentModelSchema = [...customValuesInInstrumentStageSchema, ...action.payload]
      })
      .addCase(fetchInstrumentModelSchema.pending, (state) => {
        state.status = API_STATUS.loading
      })
      .addCase(fetchInstrumentModelSchema.rejected, (state, action: any) => {
        state.status = API_STATUS.failed
        // state.error = action?.error?.message
        state.error = action.payload
      })

      .addCase(fetchInstrumentHistoryModelSchema.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.status = API_STATUS.success
        state.originalInstrumentHistoryModelSchema =  [...customValuesInInstrumentStageSchema, ...action.payload]
      })
      .addCase(fetchInstrumentHistoryModelSchema.pending, (state) => {
        state.status = API_STATUS.loading
      })
      .addCase(fetchInstrumentHistoryModelSchema.rejected, (state, action: any) => {
        state.status = API_STATUS.failed
        // state.error = action?.error?.message
        state.error = action.payload
      })

      .addCase(fetchListingExchanges.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.status = API_STATUS.success
        state.listingExchangesList = action.payload
      })
      .addCase(fetchListingExchanges.pending, (state) => {
        state.status = API_STATUS.loading
      })
      .addCase(fetchListingExchanges.rejected, (state, action: any) => {
        state.status = API_STATUS.failed
        state.error = action.payload
      })
      .addCase(fetchMarketCloseDays.fulfilled, (state, action: any) => {
        state.nasdaqHolidays = action.payload ?? []
      })
      .addCase(fetchMarketCloseDaysCustom.fulfilled, (state, action: any) => {
        state.customHolidays = action.payload ?? []
      })
  }
})
/* eslint-disable no-param-reassign */

// export const { } = organizationSlice.actions

export default utilitiesSlice.reducer

// export const selectedAllOrganizations = (state: RootState) => state[ACTION_TYPE_PARENT].organizationList
